import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookOpenCheck, CheckCircle2, XCircle, Save, ListChecks } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/* Owner: Yuki accounting link status + mapping (VAT codes, GL accounts, auto-sync). Keys live in server secrets. */
export default function YukiPanel() {
  const { t } = useLang();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["yuki-status"], queryFn: () => api.get("/yuki/status").then((r) => r.data) });
  const [f, setF] = useState({ auto_push: false, vat_type_21: "", vat_type_6: "", vat_type_0: "", vat_type_reverse: "", bank_gl: "", debtor_gl: "" });
  const [saving, setSaving] = useState(false);
  const [vatCodes, setVatCodes] = useState(null);
  useEffect(() => { if (data?.settings) setF((x) => ({ ...x, ...data.settings })); }, [data]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/yuki/settings", f); toast.success(t("saved")); qc.invalidateQueries({ queryKey: ["yuki-status"] }); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const fields = [["vat_type_21", "BTW-code 21%"], ["vat_type_6", "BTW-code 6% / 9%"], ["vat_type_0", "BTW-code 0%"], ["vat_type_reverse", "BTW-code verlegd (EU)"], ["bank_gl", t("yukiBankGl")], ["debtor_gl", t("yukiDebtorGl")]];
  return (
    <Card className="p-6 border-border space-y-4" data-testid="yuki-panel">
      <div className="page-head flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{t("accounting")}</div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><BookOpenCheck className="h-5 w-5 text-primary" /> Yuki</h2>
        </div>
        {!isLoading && (
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${data?.connected ? "st-completed" : data?.configured ? "st-overdue" : "st-paused"}`} data-testid="yuki-status">
            {data?.connected ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {data?.connected ? t("yukiConnected") : data?.configured ? t("yukiAuthFailed") : t("yukiNotConfigured")}
            {data?.country && <span className="font-mono uppercase opacity-70">· {data.country}</span>}
          </div>
        )}
      </div>
      {data && !data.configured && (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground space-y-1" data-testid="yuki-howto">
          <p className="font-medium text-foreground">{t("yukiHowtoTitle")}</p>
          <p>{t("yukiHowto1")}</p><p>{t("yukiHowto2")}</p><p>{t("yukiHowto3")}</p>
        </div>
      )}
      {data?.error && <p className="text-sm text-rose-600" data-testid="yuki-error">{data.error}</p>}
      <div className="flex items-center justify-between rounded-xl border border-border p-3">
        <div><div className="text-sm font-medium">{t("yukiAutoPush")}</div><div className="text-xs text-muted-foreground">{t("yukiAutoPushHint")}</div></div>
        <Switch checked={!!f.auto_push} onCheckedChange={(v) => setF({ ...f, auto_push: v })} data-testid="yuki-auto-push" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {fields.map(([k, lbl]) => (
          <div key={k} className="space-y-1"><Label className="text-[10px]">{lbl}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="font-mono h-10" data-testid={`yuki-${k}`} /></div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t("yukiMappingHint")}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="yuki-save"><Save className="h-4 w-4 mr-2" />{t("save")}</Button>
        <Button variant="outline" className="rounded-full" onClick={async () => { try { const { data } = await api.get("/yuki/vat-codes"); setVatCodes(data); if (data.suggested_reverse && !f.vat_type_reverse) setF({ ...f, vat_type_reverse: data.suggested_reverse }); } catch (e) { toast.error(formatApiError(e)); } }} data-testid="yuki-vat-codes"><ListChecks className="h-4 w-4 mr-2" />Btw-codes ophalen uit Yuki</Button>
      </div>
      {vatCodes && (
        <div className="rounded-lg border border-border p-3 text-xs space-y-1" data-testid="yuki-vat-codes-list">
          <div className="font-semibold">Actieve btw-codes in jouw Yuki-administratie{vatCodes.suggested_reverse ? ` · voorstel verlegd: type ${vatCodes.suggested_reverse}` : ""}</div>
          {vatCodes.codes.length === 0 && <div className="text-muted-foreground">Geen codes teruggekregen — controleer de Yuki-koppeling.</div>}
          <div className="grid sm:grid-cols-2 gap-x-4">{vatCodes.codes.map((c, i) => <div key={i} className="flex justify-between gap-2 font-mono"><span className="truncate">{c.description || c.code}</span><span className="shrink-0">{c.percentage}% · type {c.type}</span></div>)}</div>
        </div>
      )}
    </Card>
  );
}
